'use client';

import { PRODUCT_NAME } from '@/constants';
import { mergeCartWithNotification } from '@/features/account/utils/mergeCartWithNotification';
import { swal } from '@/lib/sweetalert';
import { useCartInfoAtom, useGlobalLoadingAtom } from '@/state';
import { pickErrorMessages } from '@/utils/functions';
import type { Route } from 'next';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useCallback, useRef } from 'react';
import { postSignin } from '../api/postSignin';
import { useLoggedInTokenAtom } from '../state';

/**
 * ログイン処理の共通hook
 * postSignin → signIn → カートマージ → リダイレクト を一貫して処理する
 */
export const useLogin = (callbackUrl: string) => {
  const router = useRouter();
  const [, setLoggedInToken] = useLoggedInTokenAtom();
  const [cartInfo, , clearCartInfo] = useCartInfoAtom();
  const [isLoading, startGlobalLoading, , forceStopGlobalLoading] = useGlobalLoadingAtom();
  const cartInfoRef = useRef(cartInfo);
  cartInfoRef.current = cartInfo;

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        startGlobalLoading();
        const { result } = await postSignin({ email, password });
        const signInResult = await signIn('with-token', {
          token: result.token,
          callbackUrl,
          redirect: false,
        });

        if (signInResult?.ok) {
          const currentCartInfo = cartInfoRef.current;
          if (currentCartInfo.items && currentCartInfo.items.length > 0) {
            try {
              const hasExistingItems = await mergeCartWithNotification(currentCartInfo.items, clearCartInfo);
              if (hasExistingItems) {
                forceStopGlobalLoading();
                router.push('/cart');
                return;
              }
            } catch (error) {
              await swal.messages({ messages: pickErrorMessages(error) });
              forceStopGlobalLoading();
              return;
            }
          }
          router.push(callbackUrl as Route);
          return;
        }

        forceStopGlobalLoading();
        await swal.alert({ text: 'ログインに失敗しました。' });
        // biome-ignore lint/suspicious/noExplicitAny: APIエラーの型がanyのため
      } catch (error: any) {
        forceStopGlobalLoading();
        if (error.code === 204) {
          setLoggedInToken(error.result.token);
          await swal.alert({
            text: `次の画面で「${PRODUCT_NAME}」に登録してください`,
          });
          router.push(`/auth/join?callbackUrl=${encodeURIComponent(callbackUrl)}`);
          return;
        }
        await swal.messages({ messages: pickErrorMessages(error) });
      }
    },
    [callbackUrl, clearCartInfo, forceStopGlobalLoading, router, setLoggedInToken, startGlobalLoading],
  );

  return { login, isLoading };
};
